import { useLocalizedNavigate } from '../../../hooks/useLocalizedNavigate';
import {
    formatDistanceWithLocale,
    renderEventDate,
} from '../../../utils/helpers';
import {
    Button,
    GenericError,
    Icon,
    PageLoader,
    TextField,
    Title,
    UserAvatar,
} from '../../common';
import { Feed, FeedTypes } from '../types';
import { useTranslation } from '../../../hooks/translations/TranslationProvider';
import { useAppSelector } from '../../../store/hooks';
import { COLOURS } from '../../../utils/consts';

import './feeds.css';

interface FeedsProps {
    isLoading: boolean;
    feeds: Feed[];
    error: string | null;
    onShowMoreFeedsClick: () => void;
    showLoadMoreFeedsButton: boolean;
}

const Feeds: React.FC<FeedsProps> = ({
    feeds,
    isLoading,
    error,
    showLoadMoreFeedsButton,
    onShowMoreFeedsClick,
}) => {
    const navigate = useLocalizedNavigate();
    const { t } = useTranslation();
    const auth = useAppSelector(state => state.auth);
    const language = auth.authData?.language;

    if (isLoading) {
        return <PageLoader />;
    }

    if (error) {
        return <GenericError error={error} />;
    }

    function feedClickHandler(
        type: FeedTypes,
        eventId: string,
        userId: string
    ) {
        if (type === FeedTypes.FOLLOW) {
            navigate(`/users/${userId}`);
        } else {
            navigate(`/events/${eventId}`);
        }
    }

    const userClickHandler = (userId: string) => {
        navigate(`/users/${userId}`);
    };

    if (feeds.length === 0) {
        return (
            <div className="empty-feeds">
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <Title text={t('home.emptyFeeds')} size="xl"></Title>
                </div>
                <div className="next-events-empty-actions">
                    <div
                        className="next-events-empty-action hover-green"
                        onClick={() => navigate('/finder')}
                    >
                        <div className="next-events-empty-action-icon-green">
                            <Icon
                                name="bx-map"
                                size="35px"
                                color={COLOURS.WHITE}
                            />
                        </div>
                        <div className="next-events-empty-action-text">
                            <Title text={t('common.search')} size="l" />
                            <TextField color="subtitle">
                                {t('myEvents.search')}
                            </TextField>
                        </div>
                    </div>
                    <div
                        className="next-events-empty-action hover-orange"
                        onClick={() => navigate('/create')}
                    >
                        <div className="next-events-empty-action-icon-orange">
                            <Icon
                                name="bx-calendar"
                                size="35px"
                                color={COLOURS.WHITE}
                            />
                        </div>
                        <div className="next-events-empty-action-text">
                            <Title text={t('common.create')} size="l" />
                            <TextField color="subtitle">
                                {t('myEvents.create')}
                            </TextField>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function renderFeedSubititle(type: FeedTypes) {
        switch (type) {
            case FeedTypes.CREATE_EVENT:
                return t('home.created');
            case FeedTypes.JOIN_EVENT:
                return t('home.joined');
            default:
                return null;
        }
    }

    return (
        <div className="feeds">
            <Title text={t('home.feeds')} size="xl"></Title>
            {feeds.map(feed => (
                <div className="feed-wrapper" key={feed.id}>
                    <div className="feed-user">
                        <UserAvatar
                            size="m"
                            imageSrc={feed.user.avatarUrl}
                            averageRating={feed.user.averageRating}
                            onClick={() => userClickHandler(feed.user.id)}
                        />
                        <div>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <TextField weight="bold">
                                    {feed.user.username}
                                </TextField>{' '}
                                <TextField color="subtitle">
                                    {renderFeedSubititle(feed.type)}
                                </TextField>
                            </div>
                            <TextField size="s">
                                {`${formatDistanceWithLocale(
                                    feed.createdAt,
                                    auth.authData?.language!
                                )} ${t('date.ago')}`}
                            </TextField>
                        </div>
                    </div>
                    <div className="feed-details">
                        {feed.type === FeedTypes.FOLLOW ? (
                            <>
                                <TextField>
                                    {t('home.startedFollowing')}
                                </TextField>
                                <div className="feed-details-user">
                                    <UserAvatar
                                        size="m"
                                        imageSrc={feed.follower?.avatarUrl}
                                        onClick={() =>
                                            userClickHandler(feed.follower?.id!)
                                        }
                                        averageRating={
                                            feed.follower?.averageRating
                                        }
                                    />
                                    <TextField weight="bold">
                                        {feed.follower?.username}
                                    </TextField>
                                </div>
                            </>
                        ) : (
                            <div>
                                <div>
                                    <Icon
                                        name={`bx-${feed.sportEvent?.type.toLowerCase()}`}
                                    />
                                    <div>
                                        <TextField weight="bold">
                                            {feed.sportEvent?.name}
                                        </TextField>
                                        <div className="feed-event-details">
                                            <div>
                                                <Icon
                                                    name="bx-calendar"
                                                    size="22px"
                                                    color={COLOURS.GREEN}
                                                />
                                                <TextField
                                                    size="s"
                                                    color="subtitle"
                                                >
                                                    {renderEventDate(
                                                        feed.sportEvent?.date!,
                                                        language,
                                                        {
                                                            todayLabel:
                                                                t('date.today'),
                                                            atLabel:
                                                                t('date.at'),
                                                            showTime: true,
                                                        }
                                                    )}
                                                </TextField>
                                            </div>
                                            <div>
                                                <Icon
                                                    name="bx-map"
                                                    size="22px"
                                                    color={COLOURS.GREEN}
                                                />
                                                <TextField
                                                    size="s"
                                                    color="subtitle"
                                                >
                                                    {feed.sportEvent?.place}
                                                </TextField>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    text="View Event"
                                    variant="green"
                                    size="xs"
                                    onClick={() =>
                                        feedClickHandler(
                                            feed.type,
                                            feed.sportEvent?.id!,
                                            feed.follower?.id!
                                        )
                                    }
                                ></Button>
                            </div>
                        )}
                    </div>
                </div>
            ))}
            {showLoadMoreFeedsButton && (
                <div className="event-list-btn">
                    <Button
                        text={t('common.showMore')}
                        variant="green"
                        size="md"
                        onClick={onShowMoreFeedsClick}
                        disabled={isLoading}
                    />
                </div>
            )}
        </div>
    );
};

export default Feeds;
